import { FC, useCallback, useEffect, useState } from 'react'

import { ConfirmReplyHeader } from './ConfirmReplyHeader'
import { ConfirmReplyGrid } from './ConfirmReplyGrid'
import ConfirmReplyFooter from './ConfirmReplyFooter'
import './ConfirmReply.css'
import { usePageTitle, useLeaveGuard, useAccessibleAuthority } from '../../../components/hooks'

import {
  ConfirmReplyActions,
  CommonActions,
  useAppDispatch,
  useAppSelector
} from '../../../stores'
import { ConfirmDialog, ErrorMessage, ErrorMessages } from '../../../components/parts'
import {
  PostSupplierHearingMailReqParams,
  GetSupplierHearingReplyItemSummaryRes
} from '@gateway.Supplier'
import { HearingReplyItemSummaryGateway } from '../../../gateway/supplier/HearingReplyItemSummaryGateway'
import { PutSupplierHearingStatusItemGateway } from '../../../gateway/supplier/PutSupplierHearingStatusItemGateway'
import { PostSupplierHearingMailGateway } from '../../../gateway/supplier/PostSupplierHearingMailGateway'
import { BinaryStorageContentDispositionType, BlobFileService, MessageProvider } from '../../../services'
import { Authority } from '../../../gateway/system/Authority'

export const ConfirmReply: FC = () => {
  useAccessibleAuthority(Authority.Builder, Authority.Material, Authority.Branch, Authority.Supplier)
  usePageTitle('業者ヒアリング回答確認')
  const {
    simulationYm,
    supplierCd,
    factoryCd,
    expenseCd,
    hearingSheetFlg,
    replyYm,
    branchCd,
    branchNm,
    expense,
    supplierNm,
    supplierBranchCd
  } = useAppSelector((state) => state.confirmreply.confirmReplyInfo)
  const userProp = useAppSelector((state) => state.common.userProp)

  const dispatch = useAppDispatch()

  const [supplierHearing, setSupplierHearing] =
    useState<GetSupplierHearingReplyItemSummaryRes['supplierHearing']>()
  const [confirmDialogShow, setConfirmDialogShow] = useState(false)
  const [messageId, setMessageId] = useState('')
  const [messageArgslist, setMessageArgslist] = useState([''])
  const [messageKey, setMessageKey] = useState('')
  const [notificationMessages, setNotificationMessages] = useState<ErrorMessage[]>([])
  const [errorMessages, setErrorMessages] = useState<ErrorMessage[]>([])
  const [attachmentFile, setAttachmentFile] = useState<File>()
  const [attachmentFileName, setAttachmentFileName] = useState<string>()
  const [isChanged, setIsChanged] = useState(false)

  const [data, setData] = useState<Array<
  Array<{
    value: number | null
    readOnly: boolean
  }>>>([])

  const [, resetLeaveGuard] = useLeaveGuard([isChanged], '内容が編集中です。このページを離れますか？')
  const displayStartYear = useAppSelector((state) => state.common.displayStartYear)

  // 支店ユーザかつ自支店以外の判定フラグ
  const isNotOwnBranch = (userProp?.authority === Authority.Branch && userProp?.branchCode !== branchCd)

  // 過去月の判定フラグ
  const date = new Date()
  const currentYear = date.getFullYear()
  const currentMonth = date.getMonth() + 1
  const simulatedYear = Number(simulationYm.substring(0, 4))
  const simulatedMonth = Number(simulationYm.substring(4, simulationYm.length))
  const lastMonthFlg = simulatedYear < currentYear || (simulatedYear === currentYear && simulatedMonth < currentMonth)

  /** データフォーマット */
  const dataHandle = useCallback(
    (data: GetSupplierHearingReplyItemSummaryRes) => {
      // #region 業者決定物件 start
      let supplierDecisionBuildingList: Array<{
        value: number | null
        readOnly: boolean
      }> = []

      if (
        data.supplierHearing.addStsCdSupplierDecisionBuilding !== '3'
      ) {
        supplierDecisionBuildingList = data.supplierDecisionBuilding.map(
          (item) => {
            return { value: null, readOnly: true }
          }
        )
      } else {
        supplierDecisionBuildingList = data.supplierDecisionBuilding.map(
          (item) => {
            return { value: item === 0 ? null : item, readOnly: true }
          }
        )
      }
      // #endregion 業者決定物件 end

      // #region 見積依頼物件 start
      let supplierEstimationBuildingList: Array<{
        value: number | null
        readOnly: boolean
      }> = []

      if (
        data.supplierHearing.addStsCdSupplierEstimationBuilding !== '3'
      ) {
        supplierEstimationBuildingList = data.supplierEstimationBuilding.map(
          (item) => {
            return { value: null, readOnly: true }
          }
        )
      } else {
        supplierEstimationBuildingList = data.supplierEstimationBuilding.map(
          (item) => {
            return { value: item === 0 ? null : item, readOnly: true }
          }
        )
      }
      // #endregion 見積依頼物件 end

      // #region 他社物件 start
      let otherCompanyBuildingList: Array<{
        value: number | null
        readOnly: boolean
      }> = []

      if (
        data.supplierHearing.addStsCdOtherCompanyBuilding !== '3'
      ) {
        otherCompanyBuildingList = data.otherCompanyBuilding.map((item) => {
          return { value: null, readOnly: true }
        })
      } else {
        otherCompanyBuildingList = data.otherCompanyBuilding.map((item) => {
          return { value: item === 0 ? null : item, readOnly: true }
        })
      }
      // #endregion 他社物件 end

      // #region 合计 start
      let totalWorkQuantityList: Array<{
        value: number | null
        readOnly: boolean
      }> = []

      if (
        data.supplierHearing.addStsCdSupplierDecisionBuilding !== '3' ||
        data.supplierHearing.addStsCdSupplierEstimationBuilding !== '3' ||
        data.supplierHearing.addStsCdOtherCompanyBuilding !== '3'
      ) {
        totalWorkQuantityList = data.totalWorkQuantity.map((item) => {
          return { value: 0, readOnly: true }
        })
      } else {
        totalWorkQuantityList = data.totalWorkQuantity.map((item) => {
          return { value: item === null ? 0 : item, readOnly: true }
        })
      }
      // #endregion 合计 end

      // #region 合計（他社物件含む） start
      let totalWorkQuantityWithOtherList: Array<{
        value: number | null
        readOnly: boolean
      }> = []

      if (
        data.supplierHearing.addStsCdSupplierDecisionBuilding !== '3' ||
        data.supplierHearing.addStsCdSupplierEstimationBuilding !== '3' ||
        data.supplierHearing.addStsCdOtherCompanyBuilding !== '3'
      ) {
        totalWorkQuantityWithOtherList = data.totalWorkQuantity.map((item) => {
          return { value: 0, readOnly: true }
        })
      } else {
        totalWorkQuantityWithOtherList = data.totalWorkQuantity.map((item, index) => {
          const other = otherCompanyBuildingList[index].value ?? 0
          return { value: item === null ? 0 + other : item + other, readOnly: true }
        })
      }
      // #endregion 合計（他社物件含む） end

      // #region 業者最大能力 start
      let supplierMaxCapacityList: Array<{
        value: number | null
        readOnly: boolean
      }> = []
      let ourCompanyCapacityList: Array<{
        value: number | null
        readOnly: boolean
      }> = []
      let workSufficiencyQuantityList: Array<{
        value: number | null
        readOnly: boolean
      }> = []
      let workSufficiencyRatioList: Array<{
        value: number | null
        readOnly: boolean
      }> = []

      if (
        data.supplierHearing.addStsCdSupplierMaxCapacity !== '3'
      ) {
        supplierMaxCapacityList = data.supplierMaxCapacity.map((item) => {
          return { value: null, readOnly: true }
        })
        ourCompanyCapacityList = data.ourCompanyCapacity.map((item) => {
          return { value: null, readOnly: true }
        })
        workSufficiencyQuantityList = data.workSufficiencyQuantity.map(
          (item) => {
            return { value: 0, readOnly: true }
          }
        )
        workSufficiencyRatioList = data.workSufficiencyRatio.map((item) => {
          return { value: 0, readOnly: true }
        })
      } else {
        supplierMaxCapacityList = data.supplierMaxCapacity.map((item) => {
          return { value: item === 0 ? null : item, readOnly: true }
        })
        ourCompanyCapacityList = data.ourCompanyCapacity.map((item) => {
          return { value: item === 0 ? null : item, readOnly: true }
        })
        workSufficiencyQuantityList = data.workSufficiencyQuantity.map(
          (item) => {
            return { value: item === null ? 0 : item, readOnly: true }
          }
        )
        workSufficiencyRatioList = data.workSufficiencyRatio.map((item) => {
          return { value: item === null ? 0 : item, readOnly: true }
        })
      }
      // #endregion 業者最大能力 end

      // #region 非公開物件 start
      // 非公開物件-決定
      let unpublishedBuildingDecisionList: Array<{
        value: number | null
        readOnly: boolean
      }> = []

      unpublishedBuildingDecisionList = data.unpublishedBuildingDecision.map((item) => {
        return { value: item === 0 ? null : item, readOnly: true }
      })

      // 非公開物件-未決定
      let unpublishedBuildingExpectationList: Array<{
        value: number | null
        readOnly: boolean
      }> = []

      unpublishedBuildingExpectationList = data.unpublishedBuildingExpectation.map((item) => {
        return { value: item === 0 ? null : item, readOnly: true }
      })
      // #endregion 非公開物件 end

      // #region 竣工物件 start
      let completedBuildingList: Array<{
        value: number | null
        readOnly: boolean
      }> = []

      completedBuildingList = data.completedBuilding.map((item) => {
        return { value: item === 0 ? null : item, readOnly: true }
      })
      // #endregion 竣工物件 end

      setData([
        supplierDecisionBuildingList,
        supplierEstimationBuildingList,
        completedBuildingList,
        unpublishedBuildingDecisionList,
        unpublishedBuildingExpectationList,
        otherCompanyBuildingList,
        totalWorkQuantityList,
        totalWorkQuantityWithOtherList,
        supplierMaxCapacityList,
        ourCompanyCapacityList,
        workSufficiencyQuantityList,
        workSufficiencyRatioList
      ])
    },
    []
  )

  /**
   * 業者ヒアリング状況確認-メール送信API
   */
  const handleGetHearingMailSend = (
    reqParams: PostSupplierHearingMailReqParams
  ) => {
    dispatch(CommonActions.setIsLoading(true))
    PostSupplierHearingMailGateway.postSupplierHearingMail(reqParams)
      .then((res) => {
        if (!res.error) {
          fetchHearingRepItemSmry()
        } else {
          if (Array.isArray(res.messages) && res.messages.length > 0) {
            const errorMessages = res.messages.map<ErrorMessage>((message) => {
              return {
                id: message.id,
                content: message.message
              }
            })
            setErrorMessages(errorMessages)
          }
        }
      })
      .finally(() => {
        dispatch(CommonActions.setIsLoading(false))
      })
  }

  /** ConfirmDialog イベントの確認 */
  const handleOnClickOk = () => {
    setConfirmDialogShow(false)
    if (messageKey === '修正内容登録') {
      // 修正内容登録
      //   ConfirmReplyHeaderから実行
      //   修正内容登録では回答ステータスコードの変更は行わない

      // アップロードファイルが存在する場合、アップロード処理を行う
      if (attachmentFile !== undefined) {
        new BlobFileService()
          .upload(
            'attached-files',
            attachmentFile,
            {
              dispositionType: BinaryStorageContentDispositionType.Attachment,
              filenameAlias: attachmentFileName
            }
          )
          .then((res) => {
            // アップロードファイルの設定
            setAttachmentFile(undefined)
            setAttachmentFileName(undefined)
          })
          .finally(() => {})
      }

      const reqParams = {
        data: {
          supplierHearingList: [
            {
              simulationYm,
              expenseCd,
              supplierCd,
              supplierBranchCd,
              factoryCd,
              replyComment: supplierHearing?.replyComment ?? '',
              replyNmLast: supplierHearing?.replyNmLast ?? '',
              replyNmFirst: supplierHearing?.replyNmFirst ?? '',
              fixContent: supplierHearing?.fixContent ?? '',
              attachmentFilePath: supplierHearing?.attachmentFilePath ?? '',
              updDtm: supplierHearing?.updDtm as unknown as string
            }
          ],
          replyStsCdAfterChange: supplierHearing?.replyStsCd ?? '',
          takeBackFlg: 0,
          takeOverFlg: 0,
          fixContentFlg: 1
        }
      }
      dispatch(CommonActions.setIsLoading(true))
      PutSupplierHearingStatusItemGateway.putHearingStatusItem(reqParams)
        .then((res) => {
          if (!res.error) {
            const message: ErrorMessage[] = [{
              id: 'I004',
              content: MessageProvider.provide(
                'I004', '修正内容登録'
              )
            }]
            if (isChanged) {
              resetLeaveGuard()
            }
            fetchHearingRepItemSmry()
            setNotificationMessages(message)
          } else {
            if (Array.isArray(res.messages) && res.messages.length > 0) {
              const errorMessages = res.messages.map<ErrorMessage>((message) => {
                return {
                  id: message.id,
                  content: message.message
                }
              })
              setErrorMessages(errorMessages)
            }
          }
        })
        .finally(() => {
          dispatch(CommonActions.setIsLoading(false))
        })
    }
    if (messageKey === '修正依頼') {
      // 修正依頼

      // アップロードファイルが存在する場合、アップロード処理を行う
      if (attachmentFile !== undefined) {
        new BlobFileService()
          .upload(
            'attached-files',
            attachmentFile,
            {
              dispositionType: BinaryStorageContentDispositionType.Attachment,
              filenameAlias: attachmentFileName
            }
          )
          .then((res) => {
            // アップロードファイルの設定
            setAttachmentFile(undefined)
            setAttachmentFileName(undefined)
          })
          .finally(() => {})
      }

      const reqParams = {
        data: {
          supplierHearingList: [
            {
              simulationYm,
              expenseCd,
              supplierCd,
              supplierBranchCd,
              factoryCd,
              replyComment: supplierHearing?.replyComment ?? '',
              replyNmLast: supplierHearing?.replyNmLast ?? '',
              replyNmFirst: supplierHearing?.replyNmFirst ?? '',
              fixContent: supplierHearing?.fixContent ?? '',
              attachmentFilePath: supplierHearing?.attachmentFilePath ?? '',
              updDtm: supplierHearing?.updDtm as unknown as string
            }
          ],
          replyStsCdAfterChange: '6',
          takeBackFlg: 0,
          takeOverFlg: 0,
          fixContentFlg: 0
        }
      }
      dispatch(CommonActions.setIsLoading(true))
      PutSupplierHearingStatusItemGateway.putHearingStatusItem(reqParams)
        .then((res) => {
          if (!res.error) {
            const message: ErrorMessage[] = [{
              id: 'I004',
              content: MessageProvider.provide(
                'I004', '修正依頼'
              )
            }]
            if (isChanged) {
              resetLeaveGuard()
            }
            setNotificationMessages(message)

            handleGetHearingMailSend({ data: res.data })
          } else {
            if (Array.isArray(res.messages) && res.messages.length > 0) {
              const errorMessages = res.messages.map<ErrorMessage>((message) => {
                return {
                  id: message.id,
                  content: message.message
                }
              })
              setErrorMessages(errorMessages)
            }
          }
        })
        .finally(() => {
          dispatch(CommonActions.setIsLoading(false))
        })
    }
    if (messageKey === '回答確認') {
      // 回答確認

      // アップロードファイルが存在する場合、アップロード処理を行う
      if (attachmentFile !== undefined) {
        new BlobFileService()
          .upload(
            'attached-files',
            attachmentFile,
            {
              dispositionType: BinaryStorageContentDispositionType.Attachment,
              filenameAlias: attachmentFileName
            }
          )
          .then((res) => {
            // アップロードファイルの設定
            setAttachmentFile(undefined)
            setAttachmentFileName(undefined)
          })
          .finally(() => {})
      }

      const reqParams = {
        data: {
          supplierHearingList: [
            {
              simulationYm,
              expenseCd,
              supplierCd,
              supplierBranchCd,
              factoryCd,
              replyComment: supplierHearing?.replyComment ?? '',
              replyNmLast: supplierHearing?.replyNmLast ?? '',
              replyNmFirst: supplierHearing?.replyNmFirst ?? '',
              fixContent: supplierHearing?.fixContent ?? '',
              attachmentFilePath: supplierHearing?.attachmentFilePath ?? '',
              updDtm: supplierHearing?.updDtm as unknown as string
            }
          ],
          replyStsCdAfterChange: '7',
          takeBackFlg: 0,
          takeOverFlg: 0,
          fixContentFlg: 0
        }
      }
      dispatch(CommonActions.setIsLoading(true))
      PutSupplierHearingStatusItemGateway.putHearingStatusItem(reqParams)
        .then((res) => {
          if (!res.error) {
            const message: ErrorMessage[] = [{
              id: 'I004',
              content: MessageProvider.provide(
                'I004', '回答確認'
              )
            }]

            fetchHearingRepItemSmry()
            if (isChanged) {
              resetLeaveGuard()
            }
            setNotificationMessages(message)
          } else {
            if (Array.isArray(res.messages) && res.messages.length > 0) {
              const errorMessages = res.messages.map<ErrorMessage>((message) => {
                return {
                  id: message.id,
                  content: message.message
                }
              })
              setErrorMessages(errorMessages)
            }
          }
        })
        .finally(() => {
          dispatch(CommonActions.setIsLoading(false))
        })
    }
    if (messageKey === '取戻') {
      // 取戻

      // アップロードファイルが存在する場合、アップロード処理を行う
      if (attachmentFile !== undefined) {
        new BlobFileService()
          .upload(
            'attached-files',
            attachmentFile,
            {
              dispositionType: BinaryStorageContentDispositionType.Attachment,
              filenameAlias: attachmentFileName
            }
          )
          .then((res) => {
            // アップロードファイルの設定
            setAttachmentFile(undefined)
            setAttachmentFileName(undefined)
          })
          .finally(() => {})
      }

      const reqParams = {
        data: {
          supplierHearingList: [
            {
              simulationYm,
              expenseCd,
              supplierCd,
              supplierBranchCd,
              factoryCd,
              replyComment: supplierHearing?.replyComment ?? '',
              replyNmLast: supplierHearing?.replyNmLast ?? '',
              replyNmFirst: supplierHearing?.replyNmFirst ?? '',
              fixContent: supplierHearing?.fixContent ?? '',
              attachmentFilePath: supplierHearing?.attachmentFilePath ?? '',
              updDtm: supplierHearing?.updDtm as unknown as string
            }
          ],
          replyStsCdAfterChange: '5',
          takeBackFlg: 1,
          takeOverFlg: 0,
          fixContentFlg: 0
        }
      }
      dispatch(CommonActions.setIsLoading(true))
      PutSupplierHearingStatusItemGateway.putHearingStatusItem(reqParams)
        .then((res) => {
          if (!res.error) {
            const message: ErrorMessage[] = [{
              id: 'I004',
              content: MessageProvider.provide(
                'I004', '取戻'
              )
            }]

            fetchHearingRepItemSmry()
            setNotificationMessages(message)
          } else {
            if (Array.isArray(res.messages) && res.messages.length > 0) {
              const errorMessages = res.messages.map<ErrorMessage>((message) => {
                return {
                  id: message.id,
                  content: message.message
                }
              })
              setErrorMessages(errorMessages)
            }
          }
        })
        .finally(() => {
          dispatch(CommonActions.setIsLoading(false))
        })
    }
    if (messageKey === '回答送信') {
      // 回答送信

      // アップロードファイルが存在する場合、アップロード処理を行う
      if (attachmentFile !== undefined) {
        new BlobFileService()
          .upload(
            'attached-files',
            attachmentFile,
            {
              dispositionType: BinaryStorageContentDispositionType.Attachment,
              filenameAlias: attachmentFileName
            }
          )
          .then((res) => {
            // アップロードファイルの設定
            setAttachmentFile(undefined)
            setAttachmentFileName(undefined)
          })
          .finally(() => {})
      }

      const reqParams = {
        data: {
          supplierHearingList: [
            {
              simulationYm,
              expenseCd,
              supplierCd,
              supplierBranchCd,
              factoryCd,
              replyComment: supplierHearing?.replyComment ?? '',
              replyNmLast: supplierHearing?.replyNmLast ?? '',
              replyNmFirst: supplierHearing?.replyNmFirst ?? '',
              fixContent: supplierHearing?.fixContent ?? '',
              attachmentFilePath: supplierHearing?.attachmentFilePath ?? '',
              updDtm: supplierHearing?.updDtm as unknown as string
            }
          ],
          replyStsCdAfterChange: '5',
          takeBackFlg: 0,
          takeOverFlg: 0,
          fixContentFlg: 0
        }
      }
      dispatch(CommonActions.setIsLoading(true))
      PutSupplierHearingStatusItemGateway.putHearingStatusItem(reqParams)
        .then((res) => {
          if (!res.error) {
            const message: ErrorMessage[] = [{
              id: 'I004',
              content: MessageProvider.provide(
                'I004', '回答送信'
              )
            }]

            if (isChanged) {
              resetLeaveGuard()
            }
            fetchHearingRepItemSmry()
            setNotificationMessages(message)
          } else {
            if (Array.isArray(res.messages) && res.messages.length > 0) {
              const errorMessages = res.messages.map<ErrorMessage>((message) => {
                return {
                  id: message.id,
                  content: message.message
                }
              })
              setErrorMessages(errorMessages)
            }
          }
        })
        .finally(() => {
          dispatch(CommonActions.setIsLoading(false))
        })
    }
  }

  const fetchHearingRepItemSmry = useCallback(() => {
    dispatch(CommonActions.setIsLoading(true))
    // 業者ヒアリング回答確認-サマリ取得
    HearingReplyItemSummaryGateway.getSupplierHearingReplyItemSummary(
      simulationYm,
      supplierCd,
      supplierBranchCd,
      factoryCd,
      expenseCd,
      0,
      displayStartYear
    )
      .then((res) => {
        if (!res.error) {
          setSupplierHearing(res.data.supplierHearing)
          dispatch(
            ConfirmReplyActions.setRegStatus({
              addStsCdSupplierDecisionBuilding:
                res.data.supplierHearing.addStsCdSupplierDecisionBuilding,
              addStsCdSupplierEstimationBuilding:
                res.data.supplierHearing.addStsCdSupplierEstimationBuilding,
              addStsCdOtherCompanyBuilding:
                res.data.supplierHearing.addStsCdOtherCompanyBuilding,
              addStsCdSupplierMaxCapacity:
                res.data.supplierHearing.addStsCdSupplierMaxCapacity,
              addStsCdHearingSheet: res.data.supplierHearing.addStsCdHearingSheet
            })
          )
          dispatch(
            ConfirmReplyActions.setReplyStsCd(
              res.data.supplierHearing.replyStsCd
            )
          )
          dispatch(ConfirmReplyActions.setHeaderInfo({
            replyComment: res.data.supplierHearing.replyComment,
            replyNmLast: res.data.supplierHearing.replyNmLast,
            replyNmFirst: res.data.supplierHearing.replyNmFirst,
            fixContent: res.data.supplierHearing.fixContent,
            attachmentFilePath: res.data.supplierHearing.attachmentFilePath
          }))
          dataHandle(res.data)
        } else {
          if (Array.isArray(res.messages) && res.messages.length > 0) {
            const errorMessages = res.messages.map<ErrorMessage>((message) => {
              return {
                id: message.id,
                content: message.message
              }
            })
            setErrorMessages(errorMessages)
          }
        }
      })
      .finally(() => {
        dispatch(CommonActions.setIsLoading(false))
      })
  }, [dataHandle, dispatch, expenseCd, factoryCd, simulationYm, supplierCd])

  useEffect(() => {
    // データストアチェック．業者ヒアリング遷移パラメータ．パラメータが設定されていない場合、エラーページへ遷移する
    const isPageError =
      simulationYm === '' ||
      supplierCd === '' ||
      supplierBranchCd === '' ||
      factoryCd === '' ||
      expenseCd === '' ||
      replyYm === '' ||
      expense === '' ||
      supplierNm === '' ||
      branchCd === '' ||
      branchNm === ''

    if (isPageError) {
      const pageParam = {
        simulationYm,
        supplierCd,
        supplierBranchCd,
        factoryCd,
        expenseCd,
        replyYm,
        expense,
        supplierNm,
        branchCd,
        branchNm
      }
      throw new Error(`PageError Occurred. ${JSON.stringify(pageParam)}`)
    }
  }, [
    simulationYm,
    supplierCd,
    supplierBranchCd,
    factoryCd,
    expenseCd,
    replyYm,
    expense,
    supplierNm,
    branchCd,
    branchNm
  ])

  useEffect(() => {
    fetchHearingRepItemSmry()
    return () => {
      dispatch(ConfirmReplyActions.setConfirmReplyLocation(0))
    }
  }, [])

  return (
    <>
      <ConfirmReplyHeader
        handleOnId={setMessageId}
        handleOnArgslist={setMessageArgslist}
        handleOnMessageKey={setMessageKey}
        handleOnConfirmDialogshow={setConfirmDialogShow}
        supplierHearing={supplierHearing}
        handleOnSupplierHearing={setSupplierHearing}
        setNotificationMessages={setNotificationMessages}
        setErrorMessages={setErrorMessages}
        attachmentFile={attachmentFile}
        setAttachmentFile={setAttachmentFile}
        setAttachmentFileName={setAttachmentFileName}
        lastMonthFlg={lastMonthFlg}
        isNotOwnBranch={isNotOwnBranch}
        setIsChanged={setIsChanged}
        hearingSheetFlg={hearingSheetFlg}
      />
      <ConfirmReplyGrid
        data={data}
        hearingSheetFlg={hearingSheetFlg}
        simulationYm={simulationYm}
        setNotificationMessages={setNotificationMessages}
        setErrorMessages={setErrorMessages}
        isChanged={isChanged}
        expenseCd={expenseCd}
      />
      <ConfirmReplyFooter
        handleOnId={setMessageId}
        handleOnArgslist={setMessageArgslist}
        handleOnMessageKey={setMessageKey}
        handleOnConfirmDialogshow={setConfirmDialogShow}
        setNotificationMessages={setNotificationMessages}
        setErrorMessages={setErrorMessages}
        supplierHearing={supplierHearing}
        lastMonthFlg={lastMonthFlg}
        isNotOwnBranch={isNotOwnBranch}
        isChanged={isChanged}
      />
      <ConfirmDialog
        open={confirmDialogShow}
        id={messageId}
        renderCloseButton
        args={messageArgslist}
        onClickOk={handleOnClickOk}
        onClickCancel={() => setConfirmDialogShow(false)}
      />
      <ErrorMessages type="notification" messages={notificationMessages} />
      <ErrorMessages type="dialog" messages={errorMessages} />
    </>
  )
}
